/**
 * Агрегация наблюдений синхронизации платежей в инциденты источника.
 *
 * `get_revenue_source_control_summary` отдаёт строку на пару
 * `студия × валюта`, а операционная единица — **аккаунт**: одна блокировка
 * `ln4558` даёт две строки (№18 и №90), а у `an7221` к студии №332
 * добавляется нулевая строка без номера. План
 * (`docs/REVENUE_ETL_ALERTS_IMPROVEMENT_PLAN.md`, критерии приёмки) требует
 * одной строки на аккаунт, поэтому свод собирается здесь, а не в сервисе:
 * сервис остаётся тонким транспортом, а эта логика проверяется без сети
 * (`scripts/check-revenue-audit-grouping.ts`).
 */
import type {
  MissingPaymentsControlSummaryRow,
  MissingPaymentsIssueType,
  RevenueAccountDirectoryRow,
} from '@/services/missingPaymentsControlService'
import type { RevenueSyncIncidentRow } from '@/services/revenueSyncIncidentService'

export type CurrencyTotal = {
  currency: string
  paymentCount: number
  totalAmount: number
  absAmount: number
}

export type IncidentStudioSlice = {
  studioNumber: number | null
  cityName: string | null
  address: string | null
  statusCode: string | null
  paymentCount: number
  totals: CurrencyTotal[]
}

export type SourceIncident = {
  key: string
  issueType: MissingPaymentsIssueType
  account: string
  /** Город аккаунта из справочника. `null` — в справочнике города нет. */
  accountCityName: string | null
  accountFio: string | null
  accountClass: RevenueAccountDirectoryRow['account_class'] | null
  partnerNumber: number | null
  personsFio: string | null
  studios: IncidentStudioSlice[]
  totals: CurrencyTotal[]
  paymentCount: number
  oldestPaidOn: string | null
  newestPaidOn: string | null
  lastDetectedAt: string | null
  /** Только карантин: id активной записи, чтобы дать ссылку на мастер-экран. */
  quarantineId: number | null
  reviewOverdue: boolean
  /**
   * Stable server-side incident for the operational queue. Payment journal rows
   * remain evidence only; they must not decide whether an item is still open.
   */
  stableIncident?: RevenueSyncIncidentRow
}

const ISSUE_ORDER: Record<MissingPaymentsIssueType, number> = {
  quarantined: 1,
  missing: 2,
  reissue: 3,
  deleted: 4,
  unattributed: 5,
}

/**
 * Converts one durable OPEN incident to the view model used by the audit
 * screen. Its details are still loaded from the existing payment journal by
 * account/scope; the registry intentionally does not duplicate payment rows.
 */
export function stableIncidentToSourceIncident(
  row: RevenueSyncIncidentRow,
  accounts: Map<string, RevenueAccountDirectoryRow>
): SourceIncident | null {
  const account = row.account?.trim()
  // Network incidents have no single account and therefore no safe payment
  // evidence query in the current contract. Do not invent an account from a
  // journal row; they remain available on the source master screen.
  if (!account) return null

  const directory = accounts.get(account) ?? null
  const metrics = row.metrics ?? {}
  const currencyAmounts = metrics.amount_by_currency
  const totals =
    currencyAmounts && typeof currencyAmounts === 'object' && !Array.isArray(currencyAmounts)
      ? Object.entries(currencyAmounts as Record<string, unknown>)
          .map(([currency, amount]) => ({
            currency,
            paymentCount: Number(metrics.missing_count ?? metrics.row_count ?? 0),
            totalAmount: Number(amount ?? 0),
            absAmount: Math.abs(Number(amount ?? 0)),
          }))
          .filter(total => Number.isFinite(total.absAmount) && total.absAmount > 0)
          .sort((a, b) => b.absAmount - a.absAmount)
      : []
  const studioNumber = row.studio_number == null ? null : Number(row.studio_number)
  const validStudioNumber = Number.isFinite(studioNumber) ? studioNumber : null
  const paymentCount = Number(metrics.missing_count ?? metrics.row_count ?? 0)
  const window = metrics.window
  const windowStart =
    window && typeof window === 'object' && !Array.isArray(window)
      ? String((window as Record<string, unknown>).start ?? '') || null
      : null
  const windowEnd =
    window && typeof window === 'object' && !Array.isArray(window)
      ? String((window as Record<string, unknown>).end ?? '') || null
      : null

  return {
    key: row.incident_key,
    issueType: 'missing',
    account,
    accountCityName: row.city_name ?? directory?.city_name ?? null,
    accountFio: row.account_fio ?? directory?.account_fio ?? null,
    accountClass: directory?.account_class ?? null,
    partnerNumber: null,
    personsFio: null,
    studios: validStudioNumber == null
      ? []
      : [{
          studioNumber: validStudioNumber,
          cityName: null,
          address: null,
          statusCode: null,
          paymentCount,
          totals,
        }],
    totals,
    paymentCount,
    oldestPaidOn: windowStart,
    newestPaidOn: windowEnd,
    lastDetectedAt: row.last_seen_at,
    quarantineId: null,
    reviewOverdue: row.incident_type === 'quarantine_review_overdue',
    stableIncident: row,
  }
}

function minDate(a: string | null, b: string | null): string | null {
  if (!a) return b
  if (!b) return a
  return a < b ? a : b
}

function maxDate(a: string | null, b: string | null): string | null {
  if (!a) return b
  if (!b) return a
  return a > b ? a : b
}

function addCurrency(target: Map<string, CurrencyTotal>, row: MissingPaymentsControlSummaryRow) {
  const currency = row.currency || 'RUB'
  const current = target.get(currency) ?? {
    currency,
    paymentCount: 0,
    totalAmount: 0,
    absAmount: 0,
  }
  current.paymentCount += Number(row.payment_count ?? 0)
  current.totalAmount += Number(row.total_amount ?? 0)
  current.absAmount += Number(row.abs_amount ?? 0)
  target.set(currency, current)
}

function sortTotals(totals: Map<string, CurrencyTotal>): CurrencyTotal[] {
  return Array.from(totals.values()).sort((a, b) => b.absAmount - a.absAmount)
}

export function incidentKey(issueType: MissingPaymentsIssueType, account: string): string {
  return `${issueType}:${account}`
}

/**
 * Свод строк в инциденты `(issue_type, account)`.
 *
 * Валюты **не складываются**: суммы разных валют несопоставимы, поэтому итог
 * инцидента — список по валютам, а не одно число.
 */
export function groupSourceIncidents(
  rows: MissingPaymentsControlSummaryRow[],
  accounts: Map<string, RevenueAccountDirectoryRow>
): SourceIncident[] {
  const groups = new Map<
    string,
    {
      incident: SourceIncident
      totals: Map<string, CurrencyTotal>
      studios: Map<string, { slice: IncidentStudioSlice; totals: Map<string, CurrencyTotal> }>
    }
  >()

  for (const row of rows) {
    const key = incidentKey(row.issue_type, row.account)
    const directory = accounts.get(row.account) ?? null

    let group = groups.get(key)
    if (!group) {
      group = {
        incident: {
          key,
          issueType: row.issue_type,
          account: row.account,
          accountCityName: directory?.city_name ?? null,
          accountFio: directory?.account_fio ?? null,
          accountClass: directory?.account_class ?? null,
          partnerNumber: null,
          personsFio: null,
          studios: [],
          totals: [],
          paymentCount: 0,
          oldestPaidOn: null,
          newestPaidOn: null,
          lastDetectedAt: null,
          quarantineId: null,
          reviewOverdue: false,
        },
        totals: new Map(),
        studios: new Map(),
      }
      groups.set(key, group)
    }

    const incident = group.incident
    incident.paymentCount += Number(row.payment_count ?? 0)
    incident.oldestPaidOn = minDate(incident.oldestPaidOn, row.oldest_paid_on)
    incident.newestPaidOn = maxDate(incident.newestPaidOn, row.newest_paid_on)
    incident.lastDetectedAt = maxDate(incident.lastDetectedAt, row.last_detected_at)
    incident.quarantineId = incident.quarantineId ?? row.quarantine_id
    incident.reviewOverdue = incident.reviewOverdue || Boolean(row.review_overdue)
    // Партнёр известен только у строк со студией; берём первый непустой.
    if (incident.partnerNumber == null && row.partner_number != null) {
      incident.partnerNumber = row.partner_number
      incident.personsFio = row.persons_fio
    }

    addCurrency(group.totals, row)

    const studioKey = row.studio_number == null ? 'none' : String(row.studio_number)
    let studio = group.studios.get(studioKey)
    if (!studio) {
      studio = {
        slice: {
          studioNumber: row.studio_number,
          cityName: row.city_name,
          address: row.address,
          statusCode: row.status_code,
          paymentCount: 0,
          totals: [],
        },
        totals: new Map(),
      }
      group.studios.set(studioKey, studio)
    }
    studio.slice.paymentCount += Number(row.payment_count ?? 0)
    addCurrency(studio.totals, row)
  }

  const result: SourceIncident[] = []
  for (const group of groups.values()) {
    group.incident.totals = sortTotals(group.totals)
    group.incident.studios = Array.from(group.studios.values())
      .map(studio => ({ ...studio.slice, totals: sortTotals(studio.totals) }))
      .sort((a, b) => {
        // Строка без номера всегда последняя: она деталь инцидента, а не он сам.
        if (a.studioNumber == null) return 1
        if (b.studioNumber == null) return -1
        return a.studioNumber - b.studioNumber
      })
    result.push(group.incident)
  }

  return result.sort((a, b) => {
    const byIssue = ISSUE_ORDER[a.issueType] - ISSUE_ORDER[b.issueType]
    if (byIssue !== 0) return byIssue
    if (a.reviewOverdue !== b.reviewOverdue) return a.reviewOverdue ? -1 : 1
    const aMax = a.totals[0]?.absAmount ?? 0
    const bMax = b.totals[0]?.absAmount ?? 0
    if (aMax !== bMax) return bMax - aMax
    return a.account.localeCompare(b.account)
  })
}

/**
 * Подпись инцидента в формате плана: `Город — account — №18, №90`.
 * Города нет в справочнике — пишем это явно, а не молча опускаем.
 * Студию по аккаунту НЕ подставляем: один аккаунт ведёт несколько студий.
 */
export function incidentTitle(incident: SourceIncident): string {
  const city = incident.accountCityName || 'город не указан'
  const numbered = incident.studios
    .filter(studio => studio.studioNumber != null)
    .map(studio => `№${studio.studioNumber}`)
  const studios = numbered.length > 0 ? numbered.join(', ') : 'без номера'
  return `${city} — ${incident.account} — ${studios}`
}

/** Есть ли внутри инцидента платежи без номера филиала. */
export function hasBlankStudioSlice(incident: SourceIncident): boolean {
  return incident.studios.some(studio => studio.studioNumber == null)
}

export function formatIncidentAmounts(
  totals: CurrencyTotal[],
  format: (value: number, currency: string) => string
): string {
  if (totals.length === 0) return format(0, 'RUB')
  return totals.map(total => format(total.absAmount, total.currency)).join(' · ')
}

/**
 * Лаг обнаружения в днях: сколько прошло от даты платежа до момента, когда ETL
 * заметил пропажу. Обе даты показываются рядом — без лага «платёж от 27.07,
 * обнаружено 11.08» приходится считать глазами.
 */
export function detectionLagDays(
  paidAt: string | null | undefined,
  detectedAt: string | null | undefined
): number | null {
  if (!paidAt || !detectedAt) return null
  const paid = Date.parse(`${paidAt.slice(0, 10)}T00:00:00Z`)
  const detected = Date.parse(`${detectedAt.slice(0, 10)}T00:00:00Z`)
  if (Number.isNaN(paid) || Number.isNaN(detected)) return null
  return Math.max(0, Math.round((detected - paid) / 86_400_000))
}
